import React, { useState, useEffect, useRef } from 'react';
import { Row, Col, Tabs, Input, Select, Spin, message, Slider, Upload, Progress, Button, Table, Modal } from 'antd';
import {
  PlayCircleOutlined,
  PauseCircleOutlined,
  StepBackwardOutlined,
  StepForwardOutlined,
  SearchOutlined,
  PlusOutlined,
  DeleteOutlined,
  SoundOutlined,
  InboxOutlined,
  UploadOutlined,
  AudioOutlined,
  CustomerServiceOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  ShopOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import CardContainer from '../../components/Card';
import { getApi, saveApi } from '../../helpers';
import { isSuperAdmin } from '../../helpers/permissions';
import './index.css';

const { TabPane } = Tabs;
const { Search } = Input;
const { Option } = Select;

const InstoreMusic = () => {
  // State management
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeTab, setActiveTab] = useState('library');
  const [currentStation, setCurrentStation] = useState(null);

  // Debug tab changes
  const handleTabChange = (key) => {
    console.log('Tab changed to:', key);
    setActiveTab(key);
  };
  const [queue, setQueue] = useState([]);
  const [recentlyPlayed, setRecentlyPlayed] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [countries, setCountries] = useState([]);
  const [genres, setGenres] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [volume, setVolume] = useState(70);
  const [isBuffering, setIsBuffering] = useState(false);

  // Library section toggle
  const [librarySection, setLibrarySection] = useState('live-radio');

  // Music Files state
  const [musicFiles, setMusicFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [filesLoading, setFilesLoading] = useState(false);

  // Stores state
  const [stores, setStores] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storesViewMode, setStoresViewMode] = useState('grid'); // 'grid' | 'table'
  const [selectedStore, setSelectedStore] = useState(null);
  const [showStoreModal, setShowStoreModal] = useState(false);
  const [storeNowPlaying, setStoreNowPlaying] = useState({}); // { store_id: station }

  // Audio ref
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Radio Browser API base URL
  const API_BASE = 'https://de1.api.radio-browser.info/json';

  // Fetch popular stations and stores on mount
  useEffect(() => {
    fetchPopularStations();
    fetchCountries();
    fetchGenres();
    fetchStores();

    // Load recently played from localStorage
    const saved = localStorage.getItem('recentlyPlayed');
    if (saved) {
      setRecentlyPlayed(JSON.parse(saved));
    }

    // Load store now playing from localStorage
    const storeMusic = localStorage.getItem('storeNowPlaying');
    if (storeMusic) {
      setStoreNowPlaying(JSON.parse(storeMusic));
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Fetch stores from API
  const fetchStores = async () => {
    setStoresLoading(true);
    try {
      const res = await getApi({}, "getStores", "dashboard/");
      if (res.data && res.data.length) {
        setStores(res.data);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      message.error('Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  };

  // Setup audio event listeners
  useEffect(() => {
    if (audioRef.current) {
      const audio = audioRef.current;

      audio.addEventListener('waiting', () => setIsBuffering(true));
      audio.addEventListener('playing', () => setIsBuffering(false));
      audio.addEventListener('canplay', () => setIsBuffering(false));

      return () => {
        audio.removeEventListener('waiting', () => setIsBuffering(true));
        audio.removeEventListener('playing', () => setIsBuffering(false));
        audio.removeEventListener('canplay', () => setIsBuffering(false));
      };
    }
  }, []);

  // Update volume when it changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Timer for elapsed time
  useEffect(() => {
    if (isPlaying) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [isPlaying]);

  // Debug: Log queue changes
  useEffect(() => {
    console.log('Queue state changed. Length:', queue.length);
    if (queue.length > 0) {
      console.log('Queue stations:', queue.map(s => s.name));
    }
  }, [queue]);

  // Debug: Log current station changes
  useEffect(() => {
    if (currentStation) {
      console.log('Current station changed:', currentStation.name);
    } else {
      console.log('No current station');
    }
  }, [currentStation]);

  // Debug: Log active tab changes
  useEffect(() => {
    console.log('Active tab state is now:', activeTab);
  }, [activeTab]);

  // Format elapsed time
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch instrumental stations
  const fetchPopularStations = async () => {
    setLoading(true);
    try {
      // Fetch stations with instrumental music tags
      const instrumentalTags = ['classical', 'jazz', 'instrumental', 'ambient', 'chillout', 'lounge', 'smooth jazz', 'piano', 'orchestral'];

      let allStations = [];

      // Fetch stations for each instrumental tag
      for (const tag of instrumentalTags) {
        try {
          const response = await fetch(
            `${API_BASE}/stations/search?tag=${encodeURIComponent(tag)}&hidebroken=true&order=votes&reverse=true&limit=20`
          );
          const data = await response.json();
          allStations = [...allStations, ...data];
        } catch (err) {
          console.error(`Error fetching ${tag} stations:`, err);
        }
      }

      // Remove duplicates based on stationuuid
      const uniqueStations = Array.from(
        new Map(allStations.map(station => [station.stationuuid, station])).values()
      );

      // Sort by votes and limit to top 100
      const sortedStations = uniqueStations
        .sort((a, b) => b.votes - a.votes)
        .slice(0, 100);

      setStations(sortedStations);

      if (sortedStations.length === 0) {
        message.info('No instrumental stations found');
      }
    } catch (error) {
      message.error('Failed to fetch instrumental stations');
      console.error('Error fetching stations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch countries
  const fetchCountries = async () => {
    try {
      const response = await fetch(`${API_BASE}/countries`);
      const data = await response.json();
      setCountries(data.filter(c => c.stationcount > 0).slice(0, 50));
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  // Fetch instrumental genres/tags only
  const fetchGenres = async () => {
    try {
      // Define instrumental music genres
      const instrumentalGenres = [
        'classical',
        'jazz',
        'instrumental',
        'ambient',
        'chillout',
        'lounge',
        'smooth jazz',
        'piano',
        'orchestral',
        'new age',
        'meditation',
        'relaxation',
        'easy listening',
        'world music',
        'soundtrack',
        'downtempo',
        'electronic',
        'chill',
        'nature sounds'
      ];

      // Fetch tag info for each instrumental genre
      const genrePromises = instrumentalGenres.map(async (genre) => {
        try {
          const response = await fetch(
            `${API_BASE}/tags?hidebroken=true&order=stationcount&reverse=true&limit=100`
          );
          const data = await response.json();
          return data.find(tag => tag.name.toLowerCase() === genre.toLowerCase());
        } catch (err) {
          console.error(`Error fetching ${genre}:`, err);
          return null;
        }
      });

      const results = await Promise.all(genrePromises);
      const validGenres = results
        .filter(g => g && g.stationcount > 0)
        .sort((a, b) => b.stationcount - a.stationcount);

      setGenres(validGenres);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  // Search stations (only instrumental music)
  const handleSearch = async (value) => {
    if (!value && !selectedCountry && !selectedGenre) {
      fetchPopularStations();
      return;
    }

    setLoading(true);
    try {
      const instrumentalTags = ['classical', 'jazz', 'instrumental', 'ambient', 'chillout', 'lounge', 'smooth jazz', 'piano', 'orchestral'];

      let allStations = [];

      // If a specific genre is selected, search within that genre
      if (selectedGenre) {
        let url = `${API_BASE}/stations/search?`;

        if (value) {
          url += `name=${encodeURIComponent(value)}&`;
        }
        if (selectedCountry) {
          url += `country=${encodeURIComponent(selectedCountry)}&`;
        }
        url += `tag=${encodeURIComponent(selectedGenre)}&`;
        url += 'hidebroken=true&order=votes&reverse=true&limit=50';

        const response = await fetch(url);
        const data = await response.json();
        allStations = data;
      } else {
        // Search across all instrumental tags
        for (const tag of instrumentalTags) {
          try {
            let url = `${API_BASE}/stations/search?tag=${encodeURIComponent(tag)}&`;

            if (value) {
              url += `name=${encodeURIComponent(value)}&`;
            }
            if (selectedCountry) {
              url += `country=${encodeURIComponent(selectedCountry)}&`;
            }

            url += 'hidebroken=true&order=votes&reverse=true&limit=20';

            const response = await fetch(url);
            const data = await response.json();
            allStations = [...allStations, ...data];
          } catch (err) {
            console.error(`Error searching ${tag} stations:`, err);
          }
        }

        // Remove duplicates
        const uniqueStations = Array.from(
          new Map(allStations.map(station => [station.stationuuid, station])).values()
        );
        allStations = uniqueStations.sort((a, b) => b.votes - a.votes).slice(0, 100);
      }

      setStations(allStations);

      if (allStations.length === 0) {
        message.info('No instrumental stations found');
      }
    } catch (error) {
      message.error('Failed to search stations');
      console.error('Error searching stations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add to recently played
  const addToRecentlyPlayed = (station) => {
    const updated = [
      { ...station, playedAt: new Date().toISOString() },
      ...recentlyPlayed.filter(s => s.stationuuid !== station.stationuuid)
    ].slice(0, 20); // Keep only last 20

    setRecentlyPlayed(updated);
    localStorage.setItem('recentlyPlayed', JSON.stringify(updated));
  };

  // Play station
  const playStation = (station) => {
    if (audioRef.current) {
      setIsBuffering(true);
      audioRef.current.pause();
      audioRef.current.src = station.url_resolved || station.url;
      audioRef.current.load();
      audioRef.current.play()
        .then(() => {
          setCurrentStation(station);
          setIsPlaying(true);
          setElapsedTime(0);
          addToRecentlyPlayed(station);
          console.log('Now playing:', station.name);
          message.success(`Playing: ${station.name}`);
        })
        .catch((error) => {
          message.error('Failed to play station. The stream may be offline.');
          console.error('Playback error:', error);
          setIsBuffering(false);
        });
    }
  };

  // Toggle play/pause
  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch((error) => {
            message.error('Failed to resume playback');
            console.error('Playback error:', error);
          });
      }
    }
  };

  // Add to queue
  const addToQueue = (station) => {
    if (queue.find(s => s.stationuuid === station.stationuuid)) {
      message.warning('Station already in queue');
      return;
    }
    const updatedQueue = [...queue, station];
    setQueue(updatedQueue);
    console.log('Queue updated:', updatedQueue.length, 'stations');
    message.success(`Added ${station.name} to queue`);
  };

  // Remove from queue
  const removeFromQueue = (stationId) => {
    setQueue(queue.filter(s => s.stationuuid !== stationId));
    message.success('Removed from queue');
  };

  // Play next in queue
  const playNext = () => {
    if (queue.length > 0) {
      const nextStation = queue[0];
      playStation(nextStation);
      setQueue(queue.slice(1));
    } else {
      message.info('Queue is empty');
    }
  };

  // Play previous (go back in queue if possible)
  const playPrevious = () => {
    if (recentlyPlayed.length > 1) {
      const previousStation = recentlyPlayed[1];
      playStation(previousStation);
    } else {
      message.info('No previous track available');
    }
  };

  // Generate SVG placeholder
  const generatePlaceholder = (width, height, text) => {
    const svg = `
      <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
        <rect width="100%" height="100%" fill="#4F7CFF"/>
        <text x="50%" y="50%" font-family="Arial, sans-serif" font-size="16" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">${text}</text>
      </svg>
    `;
    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };

  // Get default thumbnail
  const getStationThumbnail = (station) => {
    return station.favicon || generatePlaceholder(48, 48, 'Radio');
  };

  // ===== MUSIC FILES FUNCTIONS =====

  // Fetch music files list
  const fetchMusicFiles = async () => {
    setFilesLoading(true);
    try {
      // TODO: Replace with actual API endpoint
      // const res = await getApi({}, "getMusicFiles", "music/");
      // if (res.data) {
      //   setMusicFiles(res.data);
      // }

      // Mock data for now
      const mockFiles = [
        {
          id: '1',
          name: 'Relaxing Piano.mp3',
          artwork: generatePlaceholder(200, 200, 'Piano'),
          duration: '3:45',
          size: '5.2 MB',
          uploadedAt: new Date().toISOString(),
        },
        {
          id: '2',
          name: 'Jazz Background.wav',
          artwork: generatePlaceholder(200, 200, 'Jazz'),
          duration: '4:20',
          size: '12.8 MB',
          uploadedAt: new Date().toISOString(),
        },
      ];
      setMusicFiles(mockFiles);
    } catch (error) {
      console.error('Error fetching music files:', error);
      message.error('Failed to load music files');
    } finally {
      setFilesLoading(false);
    }
  };

  // Upload music files
  const uploadMusicFiles = async ({ fileList }) => {
    const acceptedFormats = [
      'audio/mpeg', // mp3
      'audio/wav',
      'audio/ogg',
      'audio/aac',
      'audio/flac',
      'audio/x-m4a',
      'audio/mp4',
    ];

    const validFiles = fileList.filter(file => {
      if (!acceptedFormats.includes(file.type)) {
        message.warn(`${file.name} is not a supported audio format`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      return;
    }

    setUploadProgress(10);
    setUploadingFiles(validFiles);

    try {
      // Simulate upload progress
      setTimeout(() => setUploadProgress(30), 500);
      setTimeout(() => setUploadProgress(60), 1000);

      const formData = new FormData();
      validFiles.forEach((file) => {
        formData.append('music_files', file);
      });

      // TODO: Replace with actual API endpoint
      // const headers = { "content-type": "multipart/form-data" };
      // const res = await getApi(formData, "uploadMusicFiles", "music/", headers);

      // For now, simulate successful upload
      setTimeout(() => {
        setUploadProgress(100);
        message.success(`${validFiles.length} file(s) uploaded successfully`);

        // Add to music files list (mock)
        const newFiles = validFiles.map((file, index) => ({
          id: Date.now() + index,
          name: file.name,
          artwork: generatePlaceholder(200, 200, 'Music'),
          duration: '0:00',
          size: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
          uploadedAt: new Date().toISOString(),
        }));

        setMusicFiles([...musicFiles, ...newFiles]);
        setUploadingFiles([]);
        setUploadProgress(0);
      }, 1500);

    } catch (error) {
      console.error('Error uploading files:', error);
      message.error('Failed to upload files');
      setUploadProgress(0);
      setUploadingFiles([]);
    }
  };

  // Delete music file
  const deleteMusicFile = async (fileId) => {
    try {
      // TODO: Replace with actual API endpoint
      // const res = await saveApi({ file_id: fileId }, "Music file deleted", "deleteMusicFile");

      // For now, just remove from state
      setMusicFiles(musicFiles.filter(file => file.id !== fileId));
      message.success('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      message.error('Failed to delete file');
    }
  };

  // Play music file
  const playMusicFile = (file) => {
    // TODO: Implement music file playback
    // This will require getting the actual file URL from the API
    message.info(`Playing: ${file.name} (Feature in progress)`);
  };

  // Handle store click - open modal to change music
  const handleStoreClick = (store) => {
    if (!isSuperAdmin()) {
      message.warning('Only superadmin can change store music');
      return;
    }
    setSelectedStore(store);
    setShowStoreModal(true);
  };

  // Set music for a specific store
  const setStoreMusic = (store, station) => {
    const updated = {
      ...storeNowPlaying,
      [store.store_id]: station
    };
    setStoreNowPlaying(updated);
    localStorage.setItem('storeNowPlaying', JSON.stringify(updated));
    message.success(`Now playing "${station.name}" at ${store.name}`);
    setShowStoreModal(false);
    setSelectedStore(null);
  };

  // Clear music for a store
  const clearStoreMusic = (storeId) => {
    const updated = { ...storeNowPlaying };
    delete updated[storeId];
    setStoreNowPlaying(updated);
    localStorage.setItem('storeNowPlaying', JSON.stringify(updated));
    message.success('Store music cleared');
  };

  // Load music files on mount
  useEffect(() => {
    if (librarySection === 'music-files') {
      fetchMusicFiles();
    }
  }, [librarySection]);

  // Table columns for stores
  const storeColumns = [
    {
      title: 'Store',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div className="store-table-name">
          <ShopOutlined className="store-icon" />
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'address',
      key: 'address',
      render: (text) => (
        <span className="store-address">{text || 'N/A'}</span>
      ),
    },
    {
      title: 'Now Playing',
      key: 'nowPlaying',
      render: (_, record) => {
        const playing = storeNowPlaying[record.store_id];
        return playing ? (
          <div className="store-now-playing-cell">
            <div className="playing-indicator">
              <span className="mini-visualizer-bar"></span>
              <span className="mini-visualizer-bar"></span>
              <span className="mini-visualizer-bar"></span>
            </div>
            <span className="playing-station-name">{playing.name}</span>
          </div>
        ) : (
          <span className="no-music">No music</span>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleStoreClick(record)}
          disabled={!isSuperAdmin()}
        >
          {storeNowPlaying[record.store_id] ? 'Change' : 'Set Music'}
        </Button>
      ),
    },
  ];

  return (
    <div className="instore-music-container">
      {/* Hidden audio element */}
      <audio ref={audioRef} />

      <Row gutter={24}>
        {/* Stores Section */}
        <Col xs={24} lg={14}>
          <CardContainer>
            <div className="stores-section">
              {/* Header */}
              <div className="stores-header">
                <h2 className="stores-title">
                  <ShopOutlined /> Store Music Control
                </h2>
                <div className="stores-view-toggle">
                  <button
                    className={`view-toggle-btn ${storesViewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setStoresViewMode('grid')}
                  >
                    <AppstoreOutlined />
                  </button>
                  <button
                    className={`view-toggle-btn ${storesViewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setStoresViewMode('table')}
                  >
                    <UnorderedListOutlined />
                  </button>
                </div>
              </div>

              {/* Stores Content */}
              {storesLoading ? (
                <div className="stores-loading">
                  <Spin size="large" />
                </div>
              ) : stores.length === 0 ? (
                <div className="stores-empty">
                  <ShopOutlined style={{ fontSize: 48, color: '#ccc' }} />
                  <p>No stores found</p>
                </div>
              ) : storesViewMode === 'grid' ? (
                /* Grid View */
                <div className="stores-grid">
                  {stores.map((store) => {
                    const playing = storeNowPlaying[store.store_id];
                    return (
                      <div
                        key={store.store_id}
                        className={`store-card ${isSuperAdmin() ? 'clickable' : ''}`}
                        onClick={() => handleStoreClick(store)}
                      >
                        <div className="store-card-header">
                          <ShopOutlined className="store-card-icon" />
                          <h3 className="store-card-name">{store.name}</h3>
                        </div>
                        {store.address && (
                          <p className="store-card-address">
                            <EnvironmentOutlined /> {store.address}
                          </p>
                        )}
                        {/* Now Playing Bubble */}
                        {playing ? (
                          <div className="store-music-bubble playing">
                            <div className="bubble-visualizer">
                              <span className="bubble-bar"></span>
                              <span className="bubble-bar"></span>
                              <span className="bubble-bar"></span>
                            </div>
                            <div className="bubble-info">
                              <span className="bubble-label">Now Playing</span>
                              <span className="bubble-station">{playing.name}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="store-music-bubble empty">
                            <SoundOutlined className="bubble-icon-muted" />
                            <span className="bubble-no-music">No music playing</span>
                          </div>
                        )}
                        {isSuperAdmin() && (
                          <div className="store-card-action">
                            <span>{playing ? 'Click to change' : 'Click to set music'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Table View */
                <div className="stores-table">
                  <Table
                    columns={storeColumns}
                    dataSource={stores}
                    rowKey="store_id"
                    pagination={false}
                    size="middle"
                  />
                </div>
              )}
            </div>
          </CardContainer>
        </Col>

        {/* Library/Queue/Now Playing Panel */}
        <Col xs={24} lg={10}>
          <CardContainer className="queue-panel">
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              className="queue-tabs"
              destroyInactiveTabPane={false}
            >
              {/* Library Tab */}
              <TabPane tab="Library" key="library">
                <div className="queue-content library-content">
                  {/* Section Toggle */}
                  <div className="library-section-toggle">
                    <div className="section-chips">
                      <div
                        className={`section-chip ${librarySection === 'live-radio' ? 'active' : ''}`}
                        onClick={() => setLibrarySection('live-radio')}
                      >
                        <CustomerServiceOutlined />
                        <span>Live Radio</span>
                      </div>
                      <div
                        className={`section-chip ${librarySection === 'music-files' ? 'active' : ''}`}
                        onClick={() => setLibrarySection('music-files')}
                      >
                        <AudioOutlined />
                        <span>Music Files</span>
                      </div>
                    </div>
                  </div>

                  {/* Live Radio Section */}
                  {librarySection === 'live-radio' && (
                    <div className="live-radio-section">
                      {/* Search and Filters */}
                      <div className="library-search">
                        <Search
                          placeholder="Search stations..."
                          onSearch={handleSearch}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          value={searchQuery}
                          style={{ marginBottom: 12 }}
                          prefix={<SearchOutlined />}
                        />
                        <div className="library-filters">
                          <Select
                            placeholder="Country"
                            style={{ width: '48%', marginRight: '4%' }}
                            onChange={(value) => {
                              setSelectedCountry(value);
                              setTimeout(() => handleSearch(searchQuery), 100);
                            }}
                            value={selectedCountry || undefined}
                            allowClear
                          >
                            {countries.map(country => (
                              <Option key={country.name} value={country.name}>
                                {country.name} ({country.stationcount})
                              </Option>
                            ))}
                          </Select>
                          <Select
                            placeholder="Genre"
                            style={{ width: '48%' }}
                            onChange={(value) => {
                              setSelectedGenre(value);
                              setTimeout(() => handleSearch(searchQuery), 100);
                            }}
                            value={selectedGenre || undefined}
                            allowClear
                          >
                            {genres.map(genre => (
                              <Option key={genre.name} value={genre.name}>
                                {genre.name} ({genre.stationcount})
                              </Option>
                            ))}
                          </Select>
                        </div>
                      </div>

                      {/* Stations List */}
                      <div className="stations-list">
                        {loading ? (
                          <div className="loading-container">
                            <Spin size="large" />
                          </div>
                        ) : (
                          stations.map((station) => (
                            <div key={station.stationuuid} className="queue-item station-item">
                              <img
                                src={getStationThumbnail(station)}
                                alt={station.name}
                                className="queue-thumbnail"
                                onError={(e) => {
                                  e.target.src = generatePlaceholder(48, 48, 'R');
                                }}
                              />
                              <div className="queue-item-info">
                                <p className="queue-item-title">{station.name}</p>
                                <p className="queue-item-artist">
                                  {station.country} • {station.tags || 'Radio'}
                                </p>
                              </div>
                              <div className="station-actions">
                                <button
                                  className="station-action-btn"
                                  onClick={() => playStation(station)}
                                  title="Play"
                                >
                                  <PlayCircleOutlined />
                                </button>
                                <button
                                  className="station-action-btn"
                                  onClick={() => addToQueue(station)}
                                  title="Add to Queue"
                                >
                                  <PlusOutlined />
                                </button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Music Files Section */}
                  {librarySection === 'music-files' && (
                    <div className="music-files-section">
                      {/* Upload Area */}
                      <div className="music-files-upload">
                        <Upload.Dragger
                          accept=".mp3,.wav,.ogg,.aac,.flac,.m4a,.mp4"
                          multiple
                          fileList={uploadingFiles}
                          onChange={uploadMusicFiles}
                          showUploadList={false}
                          customRequest={() => {}} // Prevent auto upload
                        >
                          <div style={{ padding: '20px' }}>
                            <InboxOutlined style={{ fontSize: '48px', color: '#4F7CFF' }} />
                            <p style={{ marginTop: 12, fontSize: '16px', fontWeight: 500 }}>
                              Click or drag audio files to upload
                            </p>
                            <p style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                              Supported formats: MP3, WAV, OGG, AAC, FLAC, M4A, MP4
                            </p>
                          </div>
                        </Upload.Dragger>

                        {uploadProgress > 0 && uploadProgress < 100 && (
                          <Progress
                            percent={uploadProgress}
                            style={{ marginTop: 16 }}
                            status="active"
                          />
                        )}
                      </div>

                      {/* Music Files List */}
                      <div className="music-files-list" style={{ marginTop: 24 }}>
                        {filesLoading ? (
                          <div className="loading-container">
                            <Spin size="large" />
                          </div>
                        ) : musicFiles.length === 0 ? (
                          <div className="empty-state" style={{ padding: '40px 20px', textAlign: 'center' }}>
                            <AudioOutlined style={{ fontSize: '48px', color: '#ccc', marginBottom: 16 }} />
                            <p style={{ color: '#666' }}>No music files uploaded yet</p>
                            <p style={{ color: '#999', fontSize: '12px' }}>Upload audio files to get started</p>
                          </div>
                        ) : (
                          <div className="music-files-grid">
                            {musicFiles.map((file) => (
                              <div key={file.id} className="music-file-card">
                                <div className="music-file-artwork">
                                  <img
                                    src={file.artwork}
                                    alt={file.name}
                                    onError={(e) => {
                                      e.target.src = generatePlaceholder(200, 200, 'M');
                                    }}
                                  />
                                  <div className="music-file-overlay">
                                    <button
                                      className="music-file-play-btn"
                                      onClick={() => playMusicFile(file)}
                                    >
                                      <PlayCircleOutlined style={{ fontSize: '32px' }} />
                                    </button>
                                  </div>
                                </div>
                                <div className="music-file-info">
                                  <p className="music-file-name" title={file.name}>
                                    {file.name}
                                  </p>
                                  <p className="music-file-meta">
                                    {file.duration} • {file.size}
                                  </p>
                                </div>
                                <div className="music-file-actions">
                                  <button
                                    className="music-file-action-btn"
                                    onClick={() => addToQueue({
                                      stationuuid: file.id,
                                      name: file.name,
                                      country: 'Local',
                                      tags: 'Music File',
                                      favicon: file.artwork
                                    })}
                                    title="Add to Queue"
                                  >
                                    <PlusOutlined />
                                  </button>
                                  <button
                                    className="music-file-action-btn delete-btn"
                                    onClick={() => deleteMusicFile(file.id)}
                                    title="Delete"
                                  >
                                    <DeleteOutlined />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabPane>

              {/* Queue Tab */}
              <TabPane tab={`Queue (${queue.length})`} key="queue">
                <div className="queue-content queue-tab-content">
                  {queue.length === 0 ? (
                    <p className="empty-state">No stations in queue. Add stations from the library.</p>
                  ) : (
                    <div className="queue-wrapper">
                      <div className="queue-header">
                        <h3 className="section-title">Up Next</h3>
                        <button
                          className="clear-queue-btn"
                          onClick={() => {
                            setQueue([]);
                            message.success('Queue cleared');
                          }}
                        >
                          Clear All
                        </button>
                      </div>
                      <div className="queue-list">
                        {queue.map((station, index) => (
                          <div key={station.stationuuid + '-' + index} className="queue-item">
                            <span className="queue-number">{index + 1}</span>
                            <img
                              src={getStationThumbnail(station)}
                              alt={station.name}
                              className="queue-thumbnail"
                              onError={(e) => {
                                e.target.src = generatePlaceholder(48, 48, 'R');
                              }}
                            />
                            <div className="queue-item-info">
                              <p className="queue-item-title">{station.name}</p>
                              <p className="queue-item-artist">
                                {station.country} • {station.tags || 'Radio'}
                              </p>
                            </div>
                            <div className="station-actions">
                              <button
                                className="station-action-btn"
                                onClick={() => {
                                  playStation(station);
                                  removeFromQueue(station.stationuuid);
                                }}
                                title="Play Now"
                              >
                                <PlayCircleOutlined />
                              </button>
                              <button
                                className="station-action-btn delete-btn"
                                onClick={() => removeFromQueue(station.stationuuid)}
                                title="Remove"
                              >
                                <DeleteOutlined />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </TabPane>

              {/* Now Playing Tab */}
              <TabPane
                tab={
                  <span className="now-playing-tab-title">
                    Now Playing
                    {isPlaying && currentStation && (
                      <span className="tab-playing-indicator">
                        <span className="tab-visualizer-bar"></span>
                        <span className="tab-visualizer-bar"></span>
                        <span className="tab-visualizer-bar"></span>
                      </span>
                    )}
                  </span>
                }
                key="now-playing"
              >
                <div className="queue-content now-playing-tab-content">
                  {currentStation ? (
                    <div className="now-playing-detail">
                      <div className="now-playing-header">
                        <img
                          src={getStationThumbnail(currentStation)}
                          alt={currentStation.name}
                          className="now-playing-image"
                          onError={(e) => {
                            e.target.src = generatePlaceholder(200, 200, 'Radio');
                          }}
                        />
                        {isPlaying && (
                          <div className="now-playing-badge">
                            <span className="live-dot"></span>
                            LIVE
                          </div>
                        )}
                      </div>

                      {/* Now Playing Info */}
                      <div className="now-playing-info">
                        <h3>{currentStation.name}</h3>
                        <p className="now-playing-subtitle">
                          {currentStation.country} • {currentStation.tags || 'Radio'}
                        </p>
                      </div>

                      {/* Music Controls */}
                      <div className="now-playing-controls">
                        <div className="np-controls-row">
                          <button className="np-control-btn" onClick={playPrevious}>
                            <StepBackwardOutlined />
                          </button>
                          <button
                            className="np-control-btn np-play-btn"
                            onClick={togglePlayPause}
                          >
                            {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                          </button>
                          <button className="np-control-btn" onClick={playNext}>
                            <StepForwardOutlined />
                          </button>
                        </div>

                        {/* Live Status */}
                        <div className="np-status">
                          {isPlaying ? (
                            <>
                              <span className="live-dot"></span>
                              <span className="np-live-text">LIVE</span>
                              <span className="np-elapsed">{formatTime(elapsedTime)}</span>
                            </>
                          ) : (
                            <span className="np-paused-text">Paused</span>
                          )}
                        </div>

                        {/* Volume Control */}
                        <div className="np-volume-control">
                          <SoundOutlined />
                          <Slider
                            value={volume}
                            onChange={setVolume}
                            style={{ flex: 1 }}
                            tooltip={{ formatter: (value) => `${value}%` }}
                          />
                          <span className="np-volume-label">{volume}%</span>
                        </div>
                      </div>

                      {/* Station Details */}
                      <div className="station-details">
                        <h4 className="section-subtitle">Station Info</h4>
                        <p className="now-playing-meta">
                          <strong>Country:</strong> {currentStation.country || 'Unknown'}
                        </p>
                        <p className="now-playing-meta">
                          <strong>Language:</strong> {currentStation.language || 'Unknown'}
                        </p>
                        <p className="now-playing-meta">
                          <strong>Bitrate:</strong> {currentStation.bitrate || 'Unknown'} kbps
                        </p>
                        <p className="now-playing-meta">
                          <strong>Codec:</strong> {currentStation.codec || 'Unknown'}
                        </p>
                        {currentStation.homepage && (
                          <p className="now-playing-meta">
                            <strong>Website:</strong>{' '}
                            <a href={currentStation.homepage} target="_blank" rel="noopener noreferrer">
                              Visit Station
                            </a>
                          </p>
                        )}
                      </div>

                      {/* Recently Played Section */}
                      {recentlyPlayed.length > 0 && (
                        <div className="recently-played-section">
                          <h4 className="section-subtitle">Recently Played</h4>
                          <div className="recently-played-list">
                            {recentlyPlayed.slice(0, 5).map((station) => (
                              <div
                                key={station.stationuuid + station.playedAt}
                                className="recently-played-item"
                                onClick={() => playStation(station)}
                              >
                                <img
                                  src={getStationThumbnail(station)}
                                  alt={station.name}
                                  className="recently-played-thumbnail"
                                  onError={(e) => {
                                    e.target.src = generatePlaceholder(32, 32, 'R');
                                  }}
                                />
                                <div className="recently-played-info">
                                  <p className="recently-played-title">{station.name}</p>
                                  <p className="recently-played-time">
                                    {new Date(station.playedAt).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="empty-state">No station currently playing</p>
                  )}
                </div>
              </TabPane>
            </Tabs>
          </CardContainer>
        </Col>
      </Row>

      {/* Floating Music Player Bar */}
      {currentStation && (
        <div className="floating-player-bar">
          <div className="floating-player-content">
            <div className="floating-player-left">
              <img
                src={getStationThumbnail(currentStation)}
                alt={currentStation.name}
                className="floating-thumbnail"
                onError={(e) => {
                  e.target.src = generatePlaceholder(56, 56, 'R');
                }}
              />
              <div className="floating-song-info">
                <p className="floating-title">{currentStation.name}</p>
                <p className="floating-artist">
                  {currentStation.country} • {isPlaying ? `🔴 LIVE • ${formatTime(elapsedTime)}` : 'Paused'}
                </p>
              </div>
            </div>

            <div className="floating-player-center">
              <div className="floating-controls">
                <button className="floating-control-btn" onClick={playPrevious}>
                  <StepBackwardOutlined />
                </button>
                <button className="floating-control-btn floating-play-btn" onClick={togglePlayPause}>
                  {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                </button>
                <button className="floating-control-btn" onClick={playNext}>
                  <StepForwardOutlined />
                </button>
              </div>
              {isPlaying && (
                <div className="floating-visualizer">
                  <span className="visualizer-bar small"></span>
                  <span className="visualizer-bar small"></span>
                  <span className="visualizer-bar small"></span>
                </div>
              )}
            </div>

            <div className="floating-player-right">
              <span className="queue-count">{queue.length} in queue</span>
              <div className="floating-volume">
                <SoundOutlined />
                <Slider
                  value={volume}
                  onChange={setVolume}
                  style={{ width: 80, margin: '0 8px' }}
                  tooltip={{ formatter: (value) => `${value}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Store Music Selection Modal */}
      <Modal
        title={
          <div className="store-modal-header">
            <ShopOutlined />
            <span>Select Music for {selectedStore?.name}</span>
          </div>
        }
        open={showStoreModal}
        onCancel={() => {
          setShowStoreModal(false);
          setSelectedStore(null);
        }}
        footer={null}
        width={600}
        className="store-music-modal"
      >
        {selectedStore && (
          <div className="store-modal-content">
            {/* Current Playing */}
            {storeNowPlaying[selectedStore.store_id] && (
              <div className="current-store-music">
                <h4>Currently Playing</h4>
                <div className="current-music-info">
                  <img
                    src={getStationThumbnail(storeNowPlaying[selectedStore.store_id])}
                    alt={storeNowPlaying[selectedStore.store_id].name}
                    className="current-music-thumb"
                    onError={(e) => {
                      e.target.src = generatePlaceholder(48, 48, 'R');
                    }}
                  />
                  <div className="current-music-details">
                    <p className="current-music-name">{storeNowPlaying[selectedStore.store_id].name}</p>
                    <p className="current-music-meta">
                      {storeNowPlaying[selectedStore.store_id].country} • {storeNowPlaying[selectedStore.store_id].tags || 'Radio'}
                    </p>
                  </div>
                  <Button
                    danger
                    size="small"
                    onClick={() => {
                      clearStoreMusic(selectedStore.store_id);
                      setShowStoreModal(false);
                    }}
                  >
                    Stop
                  </Button>
                </div>
              </div>
            )}

            {/* Station Selection */}
            <div className="station-selection">
              <h4>Select a Station</h4>
              <Search
                placeholder="Search stations..."
                onSearch={handleSearch}
                style={{ marginBottom: 16 }}
              />
              <div className="station-selection-list">
                {loading ? (
                  <div className="loading-container">
                    <Spin />
                  </div>
                ) : (
                  stations.slice(0, 20).map((station) => (
                    <div
                      key={station.stationuuid}
                      className="station-selection-item"
                      onClick={() => setStoreMusic(selectedStore, station)}
                    >
                      <img
                        src={getStationThumbnail(station)}
                        alt={station.name}
                        className="station-thumb"
                        onError={(e) => {
                          e.target.src = generatePlaceholder(40, 40, 'R');
                        }}
                      />
                      <div className="station-info">
                        <p className="station-name">{station.name}</p>
                        <p className="station-meta">
                          {station.country} • {station.tags || 'Radio'}
                        </p>
                      </div>
                      <PlayCircleOutlined className="station-play-icon" />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default InstoreMusic;
